import { defineStore } from 'pinia';
import { useApi } from '@/composables/useApi';
import { ordersApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';

export const useOrderStore = defineStore('order', {
  state: () => ({
    orders: [],
    loading: false,
    error: null,
    initialized: false,
    ordersByStatus: {
      in_review: { default: { orders: [], pagination: null } },
      production: {
        layouting: { orders: [], pagination: null },
        printing: { orders: [], pagination: null },
        operations: { orders: [], pagination: null }
      },
      quality_check: { default: { orders: [], pagination: null } },
      ready_for_release: { default: { orders: [], pagination: null } }
    },
    currentLoadingKey: null,
    userCache: {}, // Add a cache for user data
    orderPermissions: {} // Add this to store permissions
  }),

  actions: {
    async fetchOrders() {
      if (this.loading) return;
      
      this.loading = true;
      this.error = null;
      const authStore = useAuthStore();
      
      try {
        const params = {
          with: ['client', 'department', 'created_by', 'assigned_to'],
          include: ['client', 'department', 'created_by', 'assigned_to'],
          sort_by: 'created_at',
          sort_order: 'desc',
          per_page: 10000,
          limit: 10000
        };

        if (authStore.user?.role === 'artist') {
          params.assigned_to = authStore.user?.id;
        }
        
        const response = await ordersApi.getAll(params);

        let ordersData;
        if (Array.isArray(response)) {
          ordersData = response;
        } else if (response.data) {
          if (Array.isArray(response.data)) {
            ordersData = response.data;
          } else {
            ordersData = response.data.data || response.data.orders || [];
          }
        } else {
          ordersData = [];
        }
        
        const transformedOrders = await Promise.all(ordersData.map(async (order) => {
          const clientName = order.client_name || 'Unknown Client';
          const companyName = order.company_name || '';
          
          // Only use classification from client relation (clients table)
          let classification = 'regular'; // Default if client relation is not available
          
          if (order.client && order.client.classification) {
            classification = order.client.classification;
            // console.log(`[Classification Debug] fetchOrders - Order ${order.order_number}: Found client.classification = "${order.client.classification}"`);
          } else if (order.client_classification) {
            // Use client_classification field from the backend response
            classification = order.client_classification;
            // console.log(`[Classification Debug] fetchOrders - Order ${order.order_number}: Using client_classification = "${order.client_classification}"`);
          } else {
            // console.log(`[Classification Debug] fetchOrders - Order ${order.order_number}: No client.classification found, using default "regular"`);
            if (order.client) {
              // console.log(`[Classification Debug] Client data exists but missing classification:`, order.client);
            } else {
              // console.log(`[Classification Debug] No client data found for order ${order.order_number}`);
            }
          }

          // Fix for assigned_to to show full name instead of ID
          let assignedToName = order.assigned_to;
          
          // Check all possible relation names for the assigned user
          const assignedUser = order.assigned_to_user || order.user || order.assignee || order.assigned_user;
          
          if (assignedUser) {
            if (assignedUser.first_name || assignedUser.last_name) {
              assignedToName = `${assignedUser.first_name || ''} ${assignedUser.last_name || ''}`.trim();
            } else if (assignedUser.name) {
              assignedToName = assignedUser.name;
            } else if (assignedUser.username) {
              assignedToName = assignedUser.username;
            }
          } else if (order.assigned_to) {
            // Try to fetch user data directly from the API
            try {
              const userData = await this.fetchUserInfo(order.assigned_to);
              if (userData) {
                if (userData.first_name || userData.last_name) {
                  assignedToName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                } else if (userData.name) {
                  assignedToName = userData.name;
                } else if (userData.username) {
                  assignedToName = userData.username;
                }
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
            }
          }

          return {
            ...order,
            client_name: clientName,
            company_name: companyName,
            classification: classification,
            client_classification: classification,
            display_status: this.mapStatusToDisplay(order.status),
            assigned_to: assignedToName
          };
        }));

        const ordersByStatus = transformedOrders.reduce((acc, order) => {
          acc[order.status] = (acc[order.status] || 0) + 1;
          if (order.status === 'production' && order.sub_status) {
            const subKey = `production_${order.sub_status}`;
            acc[subKey] = (acc[subKey] || 0) + 1;
          }
          return acc;
        }, {});

        this.orders = transformedOrders;
        this.initialized = true;
        
      } catch (error) {
        console.error('Error fetching orders:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response?.data
        });
        this.error = error.message;
        this.orders = [];
      } finally {
        this.loading = false;
      }
    },

    async updateOrderStatus({ orderId, status, subStatus, updatedBy, formData }) {
      if (this.loading) return;
      
      this.loading = true;
      this.error = null;
      try {
        let response;
        
        // Create FormData if not provided
        if (!formData) {
          formData = new FormData();
          formData.append('status', status);
          // Only append sub_status if it's not null/undefined
          if (subStatus !== null && subStatus !== undefined) {
            formData.append('sub_status', subStatus);
          }
          formData.append('changed_by', updatedBy);
        }
        
        // Use the new permission-based API
        response = await ordersApi.updateStatusWithPermission(orderId, formData);
        
        // Find and update the order in the store
        const orderIndex = this.orders.findIndex(o => o.id === orderId);
        if (orderIndex !== -1) {
          this.orders[orderIndex] = {
            ...this.orders[orderIndex],
            status,
            sub_status: subStatus
          };
        }
        
        // Clear the permission cache for this order
        if (this.orderPermissions[orderId]) {
          delete this.orderPermissions[orderId];
        }

        return response.data;
      } catch (error) {
        console.error('Error updating order status:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          config: error.config
        });
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    mapStatusToDisplay(status, order = null) {
      // Check if order is on hold
      if (order && order.is_on_hold) {
        return 'On Hold';
      }
      
      const statusMap = {
        'production': 'In Production',
        'in_production': 'In Production',
        'in_review': 'In Review',
        'quality_check': 'Quality Check',
        'ready_release': 'Ready for Release',
        'ready_for_release': 'Ready for Release',
        'released': 'Released',
        'completed': 'Completed',
        'layouting': 'Layouting',
        'printing': 'Printing',
        'operations': 'Operations',
        'pending': 'Pending'
      };
      return statusMap[status] || status.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    async getOrders(params = {}) {
      try {
        this.loading = true;
        console.log('Fetching orders with params:', params);
        
        // Enhance the parameters with necessary includes
        const enhancedParams = {
          ...params,
          with: ['client', 'department', 'created_by_user', 'assigned_to_user', 'items'],
          include: ['client', 'department', 'created_by_user', 'assigned_to_user', 'items'],
          fields: [
            'id',
            'order_number',
            'client_id',
            'department_id',
            'status',
            'sub_status',
            'priority',
            'deadline',
            'created_at',
            'updated_at',
            'assigned_to',
            'notes',
            'client_name',
            'company_name',
            'classification',
            'display_status'
          ]
        };
        
        console.log('Enhanced params:', enhancedParams);
        const response = await ordersApi.getAll(enhancedParams);
        console.log('API Response:', response);
        
        // Handle the response format from the backend
        let ordersData = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            ordersData = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            ordersData = response.data.data;
          }
        }
        
        console.log('Orders data to process:', ordersData);
        
        if (!Array.isArray(ordersData)) {
          console.error('Invalid orders data format:', ordersData);
          throw new Error('Invalid response format from server');
        }
        
        this.orders = await Promise.all(ordersData.map(async (order) => {
          let clientName = 'Unknown Client';
          let companyName = '';
          let clientType = 'regular';
          
          // Handle client name
          if (order.client_name) {
            clientName = order.client_name;
          } else if (order.client) {
            clientName = order.client.first_name && order.client.last_name ? 
              `${order.client.first_name} ${order.client.last_name}`.trim() : 
              order.client.company_name || 'Unknown Client';
          }
          
          // Handle company name
          companyName = order.company_name || (order.client ? order.client.company_name : '') || '';
          
          // Handle client type/classification
          clientType = (order.client && order.client.classification) || 
                      order.classification || 
                      order.client_classification || 
                      'regular';
          
          // Handle department name using API-provided value; fallback to generic label
          const departmentName = order.department?.name || `Department ${order.department_id || ''}`.trim();
          
          // Handle created by name
          let createdByName = 'Unknown User';
          if (order.created_by_user_name) {
            createdByName = order.created_by_user_name;
          } else if (order.created_by_user) {
            createdByName = order.created_by_user.first_name && order.created_by_user.last_name ?
              `${order.created_by_user.first_name} ${order.created_by_user.last_name}`.trim() :
              order.created_by_user.username || 'Unknown User';
          }
          
          // Handle assigned to name
          let assignedToName = order.assigned_to;
          const assignedUser = order.assigned_to_user || order.user || order.assignee || order.assigned_user;
          
          if (assignedUser) {
            if (assignedUser.first_name || assignedUser.last_name) {
              assignedToName = `${assignedUser.first_name || ''} ${assignedUser.last_name || ''}`.trim();
            } else if (assignedUser.name) {
              assignedToName = assignedUser.name;
            } else if (assignedUser.username) {
              assignedToName = assignedUser.username;
            }
          }
          
          const displayStatus = this.mapStatusToDisplay(order.status);
          
          return {
            ...order,
            client_name: clientName,
            company_name: companyName,
            client_type: clientType,
            classification: clientType,
            client_classification: clientType,
            department: departmentName,
            created_by_name: createdByName,
            display_status: displayStatus,
            assigned_to: assignedToName,
            notes: order.notes || ''
          };
        }));

        console.log('Processed orders:', this.orders);
        return this.orders;
      } catch (error) {
        console.error('Error fetching orders:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchBoardOrders(status, subStatus = null, page = 1, search = '', filters = null, sortParams = null) {
      const key = subStatus || 'default';
      const loadingKey = `${status}-${key}-${page}`;
      
      // Skip if already loading this exact request
      if (this.currentLoadingKey === loadingKey) {
        console.log(`Already loading ${loadingKey}, skipping duplicate request`);
        return;
      }
      
      try {
        this.currentLoadingKey = loadingKey;
        this.loading = true;

        const queryParams = {
          page,
          limit: 10,
          search,
          assigned_to: useAuthStore().user?.role === 'artist' ? useAuthStore().user?.id : undefined,
          with: ['client', 'department', 'created_by', 'assigned_to_user', 'user', 'assignee', 'assigned_user', 'items', 'status_history'],
          include: ['client', 'assigned_to_user', 'user', 'assignee', 'assigned_user', 'items', 'status_history'],
          fields: 'id,order_number,client_id,department_id,status,sub_status,priority,deadline,created_at,updated_at,assigned_to,notes,client_name,company_name,classification,display_status,image_url,payment_status,total_amount,paid_amount,balance_amount,payment_method,service_type'
        };

        // Add sort parameters if provided
        if (sortParams) {
          queryParams.sort_by = sortParams.field;
          queryParams.sort_order = sortParams.direction;
          console.log(`[OrderStore] Adding sort parameters: ${sortParams.field} ${sortParams.direction}`);
        }

        let whereConditions;
        
        if (status === 'production') {
          whereConditions = [
            { field: 'status', operator: '=', value: 'production' },
            { field: 'sub_status', operator: '=', value: subStatus },
            { field: 'is_active', operator: '=', value: true },
            { field: 'is_on_hold', operator: '=', value: false }
          ];
        } else if (status === 'ready_for_release') {
          whereConditions = [
            { 
              field: 'status', 
              operator: 'IN', 
              value: ['ready_for_release', 'ready_release'] 
            },
            { field: 'is_active', operator: '=', value: true },
            { field: 'is_on_hold', operator: '=', value: false }
          ];
        } else {
          whereConditions = [
            { field: 'status', operator: '=', value: status },
            { field: 'is_active', operator: '=', value: true },
            { field: 'is_on_hold', operator: '=', value: false }
          ];
        }

        queryParams.where = JSON.stringify(whereConditions);

        console.log(`[OrderStore] Final query params for ${status}/${subStatus}:`, queryParams);
        const response = await ordersApi.getAll(queryParams);

        let ordersData;
        if (Array.isArray(response)) {
          ordersData = response;
        } else if (response.data) {
          if (Array.isArray(response.data)) {
            ordersData = response.data;
          } else {
            ordersData = response.data.data || response.data.orders || [];
          }
        } else {
          ordersData = [];
        }
        
        const transformedOrders = await Promise.all(ordersData.map(async (order) => {
          const clientName = order.client_name || 'Unknown Client';
          const companyName = order.company_name || '';
          
          // Ensure items are properly included
          const items = order.items || [];
          
          // Debug items for this order
          console.log(`[Items Debug] Order ${order.order_number} items:`, {
            'has items property': order.hasOwnProperty('items'),
            'items is array': Array.isArray(items),
            'items length': items.length,
            'items': items
          });
          
          // Only use classification from client relation (clients table)
          let classification = 'regular'; // Default if client relation is not available
          
          if (order.client && order.client.classification) {
            classification = order.client.classification;
            // console.log(`[Classification Debug] Order ${order.order_number}: Found client.classification = "${order.client.classification}"`);
          } else if (order.client_classification) {
            // Use client_classification field from the backend response
            classification = order.client_classification;
            // console.log(`[Classification Debug] Order ${order.order_number}: Using client_classification = "${order.client_classification}"`);
          } else {
            // console.log(`[Classification Debug] Order ${order.order_number}: No client.classification found, using default "regular"`);
            if (order.client) {
              // console.log(`[Classification Debug] Client data exists but missing classification:`, order.client);
            } else {
              // console.log(`[Classification Debug] No client data found for order ${order.order_number}`);
            }
          }
          
          // Fix for assigned_to to show full name instead of ID
          let assignedToName = order.assigned_to;
          
          // Check all possible relation names for the assigned user
          const assignedUser = order.assigned_to_user || order.user || order.assignee || order.assigned_user;
          
          if (assignedUser) {
            if (assignedUser.first_name || assignedUser.last_name) {
              assignedToName = `${assignedUser.first_name || ''} ${assignedUser.last_name || ''}`.trim();
            } else if (assignedUser.name) {
              assignedToName = assignedUser.name;
            } else if (assignedUser.username) {
              assignedToName = assignedUser.username;
            }
          } else if (order.assigned_to) {
            // Try to fetch user data directly from the API
            try {
              const userData = await this.fetchUserInfo(order.assigned_to);
              if (userData) {
                if (userData.first_name || userData.last_name) {
                  assignedToName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                } else if (userData.name) {
                  assignedToName = userData.name;
                } else if (userData.username) {
                  assignedToName = userData.username;
                }
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
            }
          }

          return {
            ...order,
            client_name: clientName,
            company_name: companyName,
            classification,
            assigned_to: assignedToName,
            display_status: this.mapStatusToDisplay(order.status),
            items: items // Ensure items are explicitly included
          };
        }));

        // Initialize the status and key if they don't exist
        if (!this.ordersByStatus[status]) {
          this.ordersByStatus[status] = {};
        }
        if (!this.ordersByStatus[status][key]) {
          this.ordersByStatus[status][key] = { orders: [], pagination: null };
        }

        // Get the current state
        const currentState = this.ordersByStatus[status][key];
        
        if (page === 1) {
          // For page 1, replace only the first page items but keep subsequent pages
          const ordersAfterFirstPage = currentState.orders.slice(queryParams.limit);
          const newFirstPageOrders = transformedOrders;
          
          // Combine new first page with existing subsequent pages
          this.ordersByStatus[status][key] = {
            orders: [...newFirstPageOrders, ...ordersAfterFirstPage],
            pagination: {
              total: response.data?.pagination?.total || ordersData.length,
              current_page: Math.max(1, currentState.pagination?.current_page || 1),
              per_page: queryParams.limit,
              total_pages: Math.ceil((response.data?.pagination?.total || ordersData.length) / queryParams.limit)
            }
          };
        } else {
          // For subsequent pages, append new unique orders
          const existingIds = new Set(currentState.orders.map(o => o.id));
          const uniqueNewOrders = transformedOrders.filter(order => !existingIds.has(order.id));
          
          if (uniqueNewOrders.length > 0) {
            this.ordersByStatus[status][key] = {
              orders: [...currentState.orders, ...uniqueNewOrders],
              pagination: {
                total: response.data?.pagination?.total || (currentState.orders.length + uniqueNewOrders.length),
                current_page: page,
                per_page: queryParams.limit,
                total_pages: Math.ceil((response.data?.pagination?.total || (currentState.orders.length + uniqueNewOrders.length)) / queryParams.limit)
              }
            };
          } else {
            // Even if no new orders, update pagination info
            this.ordersByStatus[status][key].pagination = {
              ...currentState.pagination,
              total: response.data?.pagination?.total || currentState.orders.length,
              current_page: page,
              total_pages: Math.ceil((response.data?.pagination?.total || currentState.orders.length) / queryParams.limit)
            };
          }
        }

        return response;
      } catch (error) {
        console.error('Error fetching board orders:', error);
        this.error = error.message;
        throw error;
      } finally {
        // Clear loading state only if this is still the current request
        if (this.currentLoadingKey === loadingKey) {
          this.loading = false;
          this.currentLoadingKey = null;
        }
      }
    },

    // Add a method to fetch user information
    async fetchUserInfo(userId) {
      if (!userId) return null;
      
      // Check cache first
      if (this.userCache[userId]) {
        return this.userCache[userId];
      }
      
      try {
        const { usersApi } = await import('@/services/api');
        
        // Try to get basic user info first (accessible to all users)
        try {
          const response = await usersApi.getBasicInfo(userId);
          
          if (response && response.data) {
            const userData = response.data;
            // Cache the user data
            this.userCache[userId] = userData;
            return userData;
          }
        } catch (basicInfoError) {
          console.log(`Could not fetch basic user info for ID ${userId}, trying full user info:`, basicInfoError);
          
          // Fall back to full user info (requires users.view permission)
          try {
            const response = await usersApi.getById(userId);
            
            if (response && response.data) {
              const userData = response.data;
              // Cache the user data
              this.userCache[userId] = userData;
              return userData;
            }
          } catch (fullInfoError) {
            console.error(`Error fetching full user info for ID ${userId}:`, fullInfoError);
            // If both methods fail, return a placeholder object with just the ID
            return { id: userId, name: `User ${userId}` };
          }
        }
        
        return null;
      } catch (error) {
        console.error(`Error in fetchUserInfo for ID ${userId}:`, error);
        // Return a placeholder object with just the ID
        return { id: userId, name: `User ${userId}` };
      }
    },

    // Add a method to preload user data
    async preloadUserData() {
      try {
        const { usersApi } = await import('@/services/api');
        const { useAuthStore } = await import('@/stores/auth');
        const authStore = useAuthStore();
        
        // Check if user has permission to view all users
        if (authStore.hasPermission('users.view')) {
          try {
            // Try to get all users (requires users.view permission)
            const response = await usersApi.getAll();
            
            if (response && response.data) {
              const users = Array.isArray(response.data) ? response.data : 
                           (response.data.data || response.data.users || []);
              
              // Cache all users
              users.forEach(user => {
                this.userCache[user.id] = user;
              });
              
              console.log(`Preloaded ${users.length} users into cache`);
              return; // Exit early if we successfully loaded all users
            }
          } catch (error) {
            console.log('Could not preload all users:', error.message);
          }
        }
        
        // If we don't have permission or the above failed, collect unique user IDs from orders and fetch them in batch
        try {
          // Collect unique user IDs from orders
          const userIds = new Set();
          
          // Check all status columns for assigned users
          Object.keys(this.ordersByStatus).forEach(status => {
            if (status === 'production') {
              Object.keys(this.ordersByStatus[status]).forEach(subStatus => {
                this.ordersByStatus[status][subStatus].orders.forEach(order => {
                  if (order.assigned_to) {
                    // Debug: Check what type of data we're adding
                    if (typeof order.assigned_to !== 'number') {
                      console.warn(`Invalid assigned_to type for order ${order.id}:`, {
                        order_id: order.id,
                        assigned_to: order.assigned_to,
                        assigned_to_type: typeof order.assigned_to,
                        assigned_to_user_name: order.assigned_to_user_name
                      });
                    }
                    userIds.add(order.assigned_to);
                  }
                });
              });
            } else {
              this.ordersByStatus[status].default.orders.forEach(order => {
                if (order.assigned_to) {
                  // Debug: Check what type of data we're adding
                  if (typeof order.assigned_to !== 'number') {
                    console.warn(`Invalid assigned_to type for order ${order.id}:`, {
                      order_id: order.id,
                      assigned_to: order.assigned_to,
                      assigned_to_type: typeof order.assigned_to,
                      assigned_to_user_name: order.assigned_to_user_name
                    });
                  }
                  userIds.add(order.assigned_to);
                }
              });
            }
          });
          
          // If we found user IDs, fetch them in batch
          if (userIds.size > 0) {
            const userIdsArray = Array.from(userIds);
            console.log(`Fetching basic info for ${userIdsArray.length} users in batch`);
            
            // Additional debugging: Check what's in the array
            const invalidIds = userIdsArray.filter(id => typeof id !== 'number');
            if (invalidIds.length > 0) {
              console.error('Found invalid user IDs in array:', invalidIds);
            }
            
            const batchResponse = await usersApi.getBasicInfoBatch(userIdsArray);
            if (batchResponse && batchResponse.data) {
              // Add users to cache
              Object.entries(batchResponse.data).forEach(([id, userData]) => {
                this.userCache[id] = userData;
              });
              
              console.log(`Added ${Object.keys(batchResponse.data).length} users to cache from batch request`);
            }
          }
        } catch (batchError) {
          console.error('Error fetching users in batch:', batchError);
          console.log('Will fetch users individually as needed');
        }
      } catch (error) {
        console.error('Error preloading user data:', error);
      }
    },

    // Handle real-time order status updates
    handleOrderStatusUpdate(orderData) {
      console.log('Received real-time order status update:', orderData);
      
      if (!orderData || !orderData.id) {
        console.error('Invalid order data received for real-time update');
        return;
      }
      
      // Find the order in all status columns
      let foundInOldStatus = false;
      let oldStatus = null;
      let oldSubStatus = null;
      
      // First, find and remove the order from its current location
      Object.keys(this.ordersByStatus).forEach(status => {
        if (status === 'production') {
          // Handle production sub-statuses
          Object.keys(this.ordersByStatus[status]).forEach(subStatus => {
            const orderIndex = this.ordersByStatus[status][subStatus].orders.findIndex(
              order => order.id === orderData.id
            );
            
            if (orderIndex !== -1) {
              foundInOldStatus = true;
              oldStatus = status;
              oldSubStatus = subStatus;
              // Remove the order from its old location
              this.ordersByStatus[status][subStatus].orders.splice(orderIndex, 1);
              
              // Update pagination count but preserve current_page and per_page
              if (this.ordersByStatus[status][subStatus].pagination) {
                const currentPagination = this.ordersByStatus[status][subStatus].pagination;
                this.ordersByStatus[status][subStatus].pagination = {
                  ...currentPagination,
                  total: Math.max(0, (currentPagination.total || 0) - 1),
                  total_pages: Math.ceil(Math.max(0, (currentPagination.total || 0) - 1) / currentPagination.per_page)
                };
              }
            }
          });
        } else {
          // Handle regular statuses
          const orderIndex = this.ordersByStatus[status].default.orders.findIndex(
            order => order.id === orderData.id
          );
          
          if (orderIndex !== -1) {
            foundInOldStatus = true;
            oldStatus = status;
            oldSubStatus = 'default';
            // Remove the order from its old location
            this.ordersByStatus[status].default.orders.splice(orderIndex, 1);
            
            // Update pagination count but preserve current_page and per_page
            if (this.ordersByStatus[status].default.pagination) {
              const currentPagination = this.ordersByStatus[status].default.pagination;
              this.ordersByStatus[status].default.pagination = {
                ...currentPagination,
                total: Math.max(0, (currentPagination.total || 0) - 1),
                total_pages: Math.ceil(Math.max(0, (currentPagination.total || 0) - 1) / currentPagination.per_page)
              };
            }
          }
        }
      });
      
      // Now add the order to its new status location
      const newStatus = orderData.status;
      const newSubStatus = orderData.sub_status || 'default';
      
      // Add highlight flag to the order data
      orderData.isHighlighted = true;
      
      // Check if we need to add to production with sub-status
      if (newStatus === 'production' && newSubStatus !== 'default') {
        // Make sure the sub-status exists in our state
        if (!this.ordersByStatus.production[newSubStatus]) {
          this.ordersByStatus.production[newSubStatus] = { orders: [], pagination: null };
        }
        
        // Add the order to the beginning of the array
        this.ordersByStatus.production[newSubStatus].orders.unshift(orderData);
        
        // Update pagination count but preserve current_page and per_page
        if (this.ordersByStatus.production[newSubStatus].pagination) {
          const currentPagination = this.ordersByStatus.production[newSubStatus].pagination;
          this.ordersByStatus.production[newSubStatus].pagination = {
            ...currentPagination,
            total: (currentPagination.total || 0) + 1,
            total_pages: Math.ceil((currentPagination.total || 0) + 1) / currentPagination.per_page
          };
        }
        
        // Remove highlight after 5 seconds
        setTimeout(() => {
          const index = this.ordersByStatus.production[newSubStatus].orders.findIndex(
            order => order.id === orderData.id
          );
          if (index !== -1) {
            this.ordersByStatus.production[newSubStatus].orders[index].isHighlighted = false;
          }
        }, 5000);
      } else {
        // Add to regular status
        if (!this.ordersByStatus[newStatus]) {
          console.warn(`Status ${newStatus} not found in ordersByStatus, skipping update`);
          return;
        }
        
        // Add the order to the beginning of the array
        this.ordersByStatus[newStatus].default.orders.unshift(orderData);
        
        // Update pagination count but preserve current_page and per_page
        if (this.ordersByStatus[newStatus].default.pagination) {
          const currentPagination = this.ordersByStatus[newStatus].default.pagination;
          this.ordersByStatus[newStatus].default.pagination = {
            ...currentPagination,
            total: (currentPagination.total || 0) + 1,
            total_pages: Math.ceil((currentPagination.total || 0) + 1) / currentPagination.per_page
          };
        }
        
        // Remove highlight after 5 seconds
        setTimeout(() => {
          const index = this.ordersByStatus[newStatus].default.orders.findIndex(
            order => order.id === orderData.id
          );
          if (index !== -1) {
            this.ordersByStatus[newStatus].default.orders[index].isHighlighted = false;
          }
        }, 5000);
      }
      
      console.log(`Order ${orderData.id} moved from ${oldStatus}/${oldSubStatus} to ${newStatus}/${newSubStatus}`);
    },
    
    // Initialize WebSocket listeners for real-time updates
    initializeRealTimeUpdates() {
      const notificationsStore = useNotificationsStore();
      
      // Make sure notifications store is initialized
      if (!notificationsStore.isInitialized) {
        console.log('Initializing notifications store for real-time order updates');
        notificationsStore.initialize();
      }
      
      // Listen for order status update events
      window.addEventListener('order-status-update', (event) => {
        if (event.detail && event.detail.order) {
          console.log('Received order-status-update event:', event.detail);
          this.handleOrderStatusUpdate(event.detail.order);
        }
      });
      
      // Listen for localStorage changes for cross-tab updates
      window.addEventListener('storage', (event) => {
        if (event.key === 'last_order_update') {
          try {
            console.log('Detected order update in another tab');
            const orderDataStr = localStorage.getItem('last_updated_order');
            if (orderDataStr) {
              const orderData = JSON.parse(orderDataStr);
              if (orderData && orderData.order) {
                console.log('Processing cross-tab order update:', orderData);
                this.handleOrderStatusUpdate(orderData.order);
              }
            }
          } catch (err) {
            console.error('Error processing cross-tab order update:', err);
          }
        }
      });
      
      // Check if there's a recent update we missed while the page was loading
      try {
        const orderDataStr = localStorage.getItem('last_updated_order');
        if (orderDataStr) {
          const orderData = JSON.parse(orderDataStr);
          const now = Date.now();
          // Only process updates that happened in the last 30 seconds
          if (orderData && orderData.timestamp && (now - orderData.timestamp < 30000)) {
            console.log('Processing recent order update from localStorage:', orderData);
            this.handleOrderStatusUpdate(orderData.order);
          }
        }
      } catch (err) {
        console.error('Error checking for recent order updates:', err);
      }
      
      console.log('Real-time order updates initialized');
    },

    // Add this new action to set order permission
    setOrderPermission(orderId, canChange, nextStatus) {
      this.orderPermissions[orderId] = { canChange, nextStatus };
    }
  }
}); 